import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2, X, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError, fmtCurrency } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// ----------------------------------------------------------------------------
// AI Depreciation Extract Dialog
// ----------------------------------------------------------------------------
// Two-phase flow (analogous to AiPdfExtractDialog, but simpler — no subpost map):
//   1) User picks an .xlsx / .csv / .pdf → POST /extract-depreciations
//      → deterministic parse for spreadsheet, Gemini for PDF
//   2) User reviews rows in an editable table → POST /import-extracted-depreciations
//      → assets persisted to db.depreciations
// ----------------------------------------------------------------------------

const ACCEPT = ".xlsx,.xlsm,.csv,.pdf";
const EMPTY_ROW = {
  asset_name: "",
  acquired_year: new Date().getFullYear(),
  initial_value: 0,
  useful_life_years: 10,
  useful_life_months: null,
  current_book_value: 0,
  residual_value: 0,
  start_date: "",
  is_active: true,
};

// Normalise names for fuzzy duplicate matching (case + whitespace + punctuation insensitive).
function normName(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

export default function AiDepExtractDialog({ open, onOpenChange, clubId, onImported }) {
  const t = useT();
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState(null); // null = no file yet, [] = extracted but user removed everything
  const [source, setSource] = useState("");
  const [notes, setNotes] = useState("");
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState("");
  // Existing asset names in this club (loaded once when the dialog opens).
  const [existingNames, setExistingNames] = useState(new Set());

  // Load existing depreciations once when the dialog opens so we can flag duplicates
  // in the review table.
  useEffect(() => {
    if (!open || !clubId) return;
    let cancelled = false;
    api.get(`/clubs/${clubId}/depreciations`).then(({ data }) => {
      if (cancelled) return;
      setExistingNames(new Set((data || []).map((d) => normName(d.asset_name))));
    }).catch(() => {
      // Non-fatal — worst case duplicates aren't flagged.
    });
    return () => { cancelled = true; };
  }, [open, clubId]);

  const reset = () => {
    setRows(null);
    setSource("");
    setNotes("");
    setFileName("");
    setUploading(false);
    setImporting(false);
  };
  const close = () => {
    reset();
    onOpenChange?.(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/clubs/${clubId}/extract-depreciations`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Duplicate-check per row: rows whose name already exists in this club default
      // to `_include=false + _mode=update` so opting them in overwrites the existing
      // asset instead of creating a copy. Non-duplicates default to `add`.
      const enriched = (data?.depreciations || []).map((r) => {
        const dup = existingNames.has(normName(r.asset_name));
        return {
          ...EMPTY_ROW,
          ...r,
          _include: !dup,
          _duplicate: dup,
          _mode: dup ? "update" : "add",
          _lid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `dep-${Date.now()}-${Math.random()}`),
        };
      });
      setRows(enriched);
      setSource(data?.source || "");
      setNotes(data?.notes || "");
      const dupCount = enriched.filter((r) => r._duplicate).length;
      if (dupCount > 0) {
        toast.success(
          t("{n} activa gevonden — {d} mogelijke duplicaten uitgezet")
            .replace("{n}", String(enriched.length))
            .replace("{d}", String(dupCount))
        );
      } else {
        toast.success(t("{n} activa gevonden").replace("{n}", String(enriched.length)));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Extractie mislukt"));
      setRows(null);
    }
    setUploading(false);
    e.target.value = "";
  };

  const updateRow = (idx, patch) => {
    setRows((cur) => cur.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, ...patch };
      // Re-evaluate duplicate flag whenever the name changes. If the user renamed a
      // duplicate row so it's no longer a duplicate → auto-switch back to "add".
      if ("asset_name" in patch) {
        const isDup = existingNames.has(normName(next.asset_name));
        next._duplicate = isDup;
        if (!isDup && next._mode === "update") next._mode = "add";
        if (isDup && next._mode === "add") next._mode = "update";
      }
      return next;
    }));
  };
  const removeRow = (idx) => setRows((cur) => cur.filter((_, i) => i !== idx));
  const toggleAll = (val) => setRows((cur) => cur.map((r) => ({ ...r, _include: val })));

  const toImport = useMemo(() => (rows || []).filter((r) => r._include && (r.asset_name || "").trim()), [rows]);
  const summary = useMemo(() => {
    const totalInitial = toImport.reduce((s, r) => s + (Number(r.initial_value) || 0), 0);
    const totalBook = toImport.reduce((s, r) => s + (Number(r.current_book_value) || 0), 0);
    const inactive = toImport.filter((r) => !r.is_active).length;
    const duplicates = (rows || []).filter((r) => r._duplicate).length;
    const toUpdate = toImport.filter((r) => r._mode === "update").length;
    const toAdd = toImport.filter((r) => r._mode !== "update").length;
    return { totalInitial, totalBook, inactive, duplicates, toUpdate, toAdd };
  }, [toImport, rows]);

  const doImport = async () => {
    if (toImport.length === 0) {
      toast.error(t("Geen rijen geselecteerd."));
      return;
    }
    setImporting(true);
    try {
      const payload = {
        depreciations: toImport.map((r) => ({
          asset_name: r.asset_name,
          acquired_year: Number(r.acquired_year) || 0,
          initial_value: Number(r.initial_value) || 0,
          useful_life_years: Number(r.useful_life_years) || 10,
          useful_life_months: r.useful_life_months ? Number(r.useful_life_months) : null,
          current_book_value: Number(r.current_book_value) || 0,
          residual_value: Number(r.residual_value) || 0,
          start_date: r.start_date || null,
          is_active: Boolean(r.is_active),
          _mode: r._mode || "add",
        })),
      };
      const { data } = await api.post(`/clubs/${clubId}/import-extracted-depreciations`, payload);
      const inserted = Number(data?.inserted || 0);
      const updated = Number(data?.updated || 0);
      if (updated > 0 && inserted > 0) {
        toast.success(
          t("{a} nieuw + {u} bijgewerkt")
            .replace("{a}", String(inserted))
            .replace("{u}", String(updated))
        );
      } else if (updated > 0) {
        toast.success(t("{u} activa bijgewerkt").replace("{u}", String(updated)));
      } else {
        toast.success(t("{n} activa geïmporteerd").replace("{n}", String(inserted)));
      }
      onImported?.();
      close();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Import mislukt"));
    }
    setImporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange?.(true) : close())}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-5 border-b-2 border-foreground">
          <DialogTitle className="font-display uppercase text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {t("AI-import afschrijvingen")}
          </DialogTitle>
          <DialogDescription>
            {t("Upload een Twinfield / boekhoud-export (.xlsx of .csv) of een PDF activaregister. We extraheren automatisch startdatum, levensduur, boekwaarde en status.")}
          </DialogDescription>
        </DialogHeader>

        <div className="p-5 flex-1 overflow-auto space-y-4" data-testid="ai-dep-extract-body">
          {rows === null ? (
            <div className="border-2 border-dashed border-foreground p-10 text-center bg-muted/30">
              <Sparkles className="w-8 h-8 mx-auto text-primary mb-3" />
              <p className="font-display uppercase text-lg tracking-tight mb-2">{t("Kies een bestand om te starten")}</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                {t("XLSX en CSV worden direct gelezen. PDF wordt door Gemini geanalyseerd (~10-30s).")}
              </p>
              <label
                data-testid="ai-dep-extract-upload"
                className={`inline-flex items-center gap-2 btn-brutal bg-primary text-primary-foreground px-5 py-2.5 font-display uppercase font-bold tracking-wider cursor-pointer ${uploading ? "opacity-70 pointer-events-none" : ""}`}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? t("Bezig met extractie…") : t("Bestand kiezen")}
                <input type="file" accept={ACCEPT} onChange={handleFile} className="hidden" />
              </label>
              {fileName && <p className="text-xs text-muted-foreground mt-3">{fileName}</p>}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="stat-label mb-1">
                    {t("Bron")}: {source} · {t("{n} activa gevonden").replace("{n}", String(rows.length))}
                    {notes && ` · ${notes}`}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono-num">
                    {t("Totaal inkoopwaarde")}: {fmtCurrency(summary.totalInitial)} ·{" "}
                    {t("Totaal boekwaarde")}: {fmtCurrency(summary.totalBook)} ·{" "}
                    {t("{n} inactief").replace("{n}", String(summary.inactive))}
                    {summary.duplicates > 0 && (
                      <>
                        {" · "}
                        <span
                          className="text-primary font-bold"
                          data-testid="ai-dep-duplicate-summary"
                          title={t("Duplicaten met een vinkje overschrijven de bestaande rij.")}
                        >
                          {t("{n} duplicaat").replace("{n}", String(summary.duplicates))}
                          {summary.toUpdate > 0 && ` (${summary.toUpdate} ${t("wordt bijgewerkt")})`}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleAll(true)}
                    data-testid="ai-dep-select-all"
                    className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs"
                  >
                    {t("Alles aan")}
                  </button>
                  <button
                    onClick={() => toggleAll(false)}
                    data-testid="ai-dep-deselect-all"
                    className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs"
                  >
                    {t("Alles uit")}
                  </button>
                </div>
              </div>

              <div className="border-2 border-foreground overflow-x-auto bg-card">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-foreground text-background">
                      <th className="px-2 py-2 w-8 text-center">{t("In")}</th>
                      <th className="px-2 py-2 text-left font-display uppercase tracking-widest">{t("Naam")}</th>
                      <th className="px-2 py-2 text-left font-display uppercase tracking-widest">{t("Startdatum")}</th>
                      <th className="px-2 py-2 text-right font-display uppercase tracking-widest">{t("Initieel")}</th>
                      <th className="px-2 py-2 text-right font-display uppercase tracking-widest">{t("Levensduur (mnd)")}</th>
                      <th className="px-2 py-2 text-right font-display uppercase tracking-widest">{t("Boekwaarde")}</th>
                      <th className="px-2 py-2 text-right font-display uppercase tracking-widest">{t("Restwaarde")}</th>
                      <th className="px-2 py-2 text-center font-display uppercase tracking-widest">{t("Actief?")}</th>
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r._lid || `legacy-${i}`} className={`${r._include ? "" : "opacity-40 bg-muted/40"} border-b border-foreground/10`}>
                        <td className="px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={Boolean(r._include)}
                            onChange={(e) => updateRow(i, { _include: e.target.checked })}
                            className="w-4 h-4 cursor-pointer"
                            data-testid={`ai-dep-include-${i}`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-1.5">
                            <input
                              value={r.asset_name || ""}
                              onChange={(e) => updateRow(i, { asset_name: e.target.value })}
                              className="flex-1 min-w-0 bg-transparent border-b border-transparent hover:border-foreground/30 focus:border-primary focus:outline-none px-1 py-0.5"
                            />
                            {r._duplicate && (
                              r._include ? (
                                <span
                                  data-testid={`ai-dep-update-badge-${i}`}
                                  title={t("De bestaande rij wordt overschreven met deze nieuwe waarden.")}
                                  className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 border border-primary text-primary font-display uppercase font-bold text-[10px] tracking-widest bg-primary/5"
                                >
                                  <AlertCircle className="w-3 h-3" />
                                  {t("Wordt bijgewerkt")}
                                </span>
                              ) : (
                                <span
                                  data-testid={`ai-dep-duplicate-badge-${i}`}
                                  title={t("Er bestaat al een activum met deze naam in deze club.")}
                                  className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 border border-destructive text-destructive font-display uppercase font-bold text-[10px] tracking-widest bg-destructive/5"
                                >
                                  <AlertCircle className="w-3 h-3" />
                                  {t("Bestaat al")}
                                </span>
                              )
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="date"
                            value={r.start_date || ""}
                            onChange={(e) => updateRow(i, { start_date: e.target.value })}
                            className="w-full bg-transparent border-b border-transparent hover:border-foreground/30 focus:border-primary focus:outline-none px-1 py-0.5 font-mono-num text-xs"
                          />
                        </td>
                        <td className="px-2 py-1 text-right">
                          <input
                            type="number"
                            value={r.initial_value ?? 0}
                            onChange={(e) => updateRow(i, { initial_value: e.target.value })}
                            className="w-24 text-right bg-transparent border-b border-transparent hover:border-foreground/30 focus:border-primary focus:outline-none px-1 py-0.5 font-mono-num"
                          />
                        </td>
                        <td className="px-2 py-1 text-right">
                          <input
                            type="number"
                            value={r.useful_life_months ?? ""}
                            placeholder={String((r.useful_life_years || 10) * 12)}
                            onChange={(e) => updateRow(i, { useful_life_months: e.target.value })}
                            className="w-16 text-right bg-transparent border-b border-transparent hover:border-foreground/30 focus:border-primary focus:outline-none px-1 py-0.5 font-mono-num"
                          />
                        </td>
                        <td className="px-2 py-1 text-right">
                          <input
                            type="number"
                            value={r.current_book_value ?? 0}
                            onChange={(e) => updateRow(i, { current_book_value: e.target.value })}
                            className="w-24 text-right bg-transparent border-b border-transparent hover:border-foreground/30 focus:border-primary focus:outline-none px-1 py-0.5 font-mono-num"
                          />
                        </td>
                        <td className="px-2 py-1 text-right">
                          <input
                            type="number"
                            value={r.residual_value ?? 0}
                            onChange={(e) => updateRow(i, { residual_value: e.target.value })}
                            className="w-20 text-right bg-transparent border-b border-transparent hover:border-foreground/30 focus:border-primary focus:outline-none px-1 py-0.5 font-mono-num"
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={Boolean(r.is_active)}
                            onChange={(e) => updateRow(i, { is_active: e.target.checked })}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <button
                            onClick={() => removeRow(i)}
                            className="p-1 hover:bg-destructive hover:text-white"
                            title={t("Verwijderen")}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-2 py-8 text-center text-muted-foreground">
                          {t("Alle rijen verwijderd.")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="p-5 border-t-2 border-foreground flex-shrink-0">
          <button
            onClick={close}
            data-testid="ai-dep-extract-cancel"
            className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold tracking-wider inline-flex items-center gap-2"
          >
            <X className="w-4 h-4" /> {t("Annuleren")}
          </button>
          {rows !== null && (
            <button
              onClick={doImport}
              disabled={importing || toImport.length === 0}
              data-testid="ai-dep-extract-import"
              className="btn-brutal bg-primary text-primary-foreground px-5 py-2 font-display uppercase font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {importing
                ? t("Importeren…")
                : summary.toUpdate > 0 && summary.toAdd > 0
                  ? t("Voeg {a} toe · Werk {u} bij")
                      .replace("{a}", String(summary.toAdd))
                      .replace("{u}", String(summary.toUpdate))
                  : summary.toUpdate > 0
                    ? t("Werk {u} bij").replace("{u}", String(summary.toUpdate))
                    : t("Importeer {n}").replace("{n}", String(toImport.length))}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
